import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { RxState } from '@rx-angular/state';
import { BehaviorSubject, Observable } from 'rxjs';
import { GlobalState, GLOBAL_RX_STATE } from 'src/app/app-state.tokens';
import { AppService } from '../app.service';

// TODO: Sostituire .toPromise()

@Injectable({ providedIn: 'root' })
export class HttpIoService {
  private readonly _basectrl;
  public communicating$ = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient, @Inject(GLOBAL_RX_STATE) private globalState: RxState<GlobalState>, @Inject('BACKEND_URL') private backendUrl: string,) {
    const apiUrl = `${backendUrl}/api`;
    this.globalState.set({ apiUrl });
    this._basectrl = apiUrl.trim().endsWith('/') ? apiUrl.trim() : `${apiUrl.trim()}/`;
    this.globalState.connect("httpComm", this.communicating$);
  }

  /**
   * Alza il LED di attività (`httpComm`, letto da `AppStatusLedsComponent`) e lo RIABBASSA sempre.
   *
   * Prima ogni metodo faceva `next(true)` … `next(false)` in sequenza, senza `finally`: al primo
   * errore — backend giù, 500, timeout — la `next(false)` non veniva mai eseguita e il LED restava
   * acceso per il resto della vita dell'app. Un indicatore che mente è peggio di nessun indicatore.
   *
   * La guardia sta QUI e non nei singoli metodi: erano dodici call-site con lo stesso difetto, e
   * dodici `try/finally` copiati sono dodici occasioni di dimenticarne uno il prossimo giro.
   */
  private async tracked<T>(call: () => Promise<T>): Promise<T> {
    this.communicating$.next(true);
    try {
      return await call();
    } finally {
      this.communicating$.next(false);
    }
  }

  async patchWithoutResponse<DTO>(ctrl: string, data: DTO): Promise<void> {
    await this.http.patch(this.endPoint(ctrl), data).toPromise();
  }

  async patch<RES, DTO>(ctrl: string, data: DTO): Promise<RES> {
    return await this.http.patch<RES>(this.endPoint(ctrl), data).toPromise();
  }

  async postWithoutResponse<DTO>(ctrl: string, data: DTO): Promise<void> {
    await this.http.post(this.endPoint(ctrl), data).toPromise();
  }

  async post<RES, DTO>(ctrl: string, data: DTO): Promise<RES> {
    return this.tracked(() => this.http.post<RES>(this.endPoint(ctrl), data).toPromise());
  }

  async put<RES, DTO>(ctrl: string, data: DTO): Promise<RES> {
    return this.tracked(() => this.http.put<RES>(this.endPoint(ctrl), data).toPromise());
  }

  async getWithoutResponse(ctrl: string): Promise<void> {
    await this.tracked(() => this.http.get(this.endPoint(ctrl)).toPromise());
  }

  async get<RES>(ctrl: string): Promise<RES> {
    const rv = await this.tracked(() => this.http.get<RES>(this.endPoint(ctrl)).toPromise());
    console.log('get', rv);
    return rv;
  }

  async externalGet<RES>(url: string): Promise<RES> {
    return this.tracked(() => this.http.get<RES>(url).toPromise());
  }

  item$<T>(ctrl: string, id: string | number): Observable<T> {
    return this.http.get<T>(`${this.endPoint(ctrl)}/${id}`);
  }

  items$<T>(ctrl: string): Observable<T[]> {
    return this.http.get<T[]>(this.endPoint(ctrl));
  }

  /**
   * Esegue una chiamata GET a {URL_API}/{ctrl}/{id}
   * @param {string} ctrl - è l'url del controller
   * @param {string} id - è l'identificativo della risorsa
   */
  async fetchItem<T>(ctrl: string, id: string | number): Promise<T> {
    return this.tracked(() => this.item$<T>(ctrl, id).toPromise());
  }

  async fetchItems<T>(ctrl: string): Promise<T[]> {
    return this.tracked(() => this.items$<T>(ctrl).toPromise());
  }

  async postItem<T, DTO>(ctrl: string, dto: DTO): Promise<T> {
    return this.tracked(() => this.http.post<T>(this.endPoint(ctrl), dto).toPromise());
  }

  async patchItem<T, DTO>(ctrl: string, id: string | number, dto: DTO): Promise<T> {
    return this.tracked(() => this.http.patch<T>(`${this.endPoint(ctrl)}/${id}`, dto).toPromise());
  }

  /** `RES` non specificato = risposta ignorata (resta `unknown`, non `any`). */
  async deleteItem<RES>(ctrl: string, id: string | number): Promise<RES> {
    return this.tracked(() => this.http.delete<RES>(`${this.endPoint(ctrl)}/${id}`).toPromise());
  }

  /**
   * This method returns the url to controller with the correct backend url, based on the app enviroment
   * 
   * @param ctrl : the path to the controller
   * @returns : correct path
   */
  public endPoint(ctrl: string): string {
    let _ctrl = ctrl.trim();
    _ctrl = _ctrl.startsWith('/') ? _ctrl.substring(1) : _ctrl;
    return `${this._basectrl}${_ctrl}`;
  }
}
